"use client"

import { useCallback, useEffect, useState } from "react"
import {
  Bot,
  Activity,
  AlertCircle,
  Database,
  HardDrive,
  Clock,
  RefreshCw,
  Link2,
  Server,
  Sparkles,
} from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AgentNarrativeMarkdown } from "@/components/dashboard/agent-narrative-markdown"
import { CycleProgressPanel } from "@/components/dashboard/cycle-progress-panel"
import type { AgentFleetItem, AgentFleetResponse, AgentHealthDetailResponse } from "@/lib/agent-force"
import { fetchAgentHealth } from "@/lib/agent-force"

const HEALTH_DETAIL_POLL_MS = 30_000

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "Never"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

function formatDurationSeconds(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return "—"
  if (sec < 60) return `${Math.round(sec)}s`
  if (sec < 3600) return `${Math.round(sec / 60)}m`
  return `${(sec / 3600).toFixed(1)}h`
}

function healthStyle(health: AgentFleetItem["health"]) {
  if (health === "ok") return { badge: "text-positive border-positive/40 bg-positive/10", label: "Healthy" }
  if (health === "degraded") return { badge: "text-warning border-warning/40 bg-warning/10", label: "Degraded" }
  return { badge: "text-muted-foreground border-border bg-secondary/50", label: "Offline" }
}

function freshnessBadge(verdict: AgentFleetItem["freshness_verdict"]) {
  if (verdict === "fresh") return { cls: "text-positive border-positive/40 bg-positive/10", label: "Data fresh" }
  if (verdict === "stale") return { cls: "text-warning border-warning/40 bg-warning/10", label: "Possibly stale" }
  return { cls: "text-muted-foreground border-border bg-secondary/50", label: "No runs logged" }
}

function FleetNarrativePanel({
  label,
  updatedAt,
  body,
}: {
  label: string
  updatedAt: string | null | undefined
  body: string
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border/80 bg-secondary/15 p-3">
      <p className="text-[11px] font-medium text-primary mb-1">{label}</p>
      <p className="text-[11px] text-muted-foreground mb-2">{formatWhen(updatedAt)}</p>
      <div className="max-h-[min(50vh,28rem)] overflow-y-auto overflow-x-hidden overscroll-y-contain pr-2 [scrollbar-gutter:stable]">
        <AgentNarrativeMarkdown>{body}</AgentNarrativeMarkdown>
      </div>
    </div>
  )
}

function AgentHealthModal({
  open,
  onOpenChange,
  agentKey,
  fleet,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  agentKey: string | null
  fleet?: AgentFleetResponse | null
}) {
  const [detail, setDetail] = useState<AgentHealthDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadDetail = useCallback(async () => {
    if (!agentKey) return
    setLoading(true)
    try {
      const cardLast =
        fleet?.agents.find((a) => a.agent_key === agentKey)?.last_executed_at ?? null
      const d = await fetchAgentHealth(agentKey, { clientCardLastExecutedAt: cardLast })
      setDetail(d)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load agent health")
    } finally {
      setLoading(false)
    }
  }, [agentKey, fleet])

  useEffect(() => {
    if (!open || !agentKey) {
      setDetail(null)
      setError(null)
      return
    }
    void loadDetail()
    const id = window.setInterval(() => void loadDetail(), HEALTH_DETAIL_POLL_MS)
    return () => window.clearInterval(id)
  }, [open, agentKey, loadDetail])

  const cardAgent = agentKey ? fleet?.agents.find((a) => a.agent_key === agentKey) : null
  const titleName = detail?.display_name ?? cardAgent?.display_name ?? agentKey ?? "Agent"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="max-h-[min(88vh,920px)] w-[calc(100%-1.5rem)] max-w-3xl overflow-y-auto border-border sm:max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
            <Bot className="h-5 w-5 shrink-0 text-primary" />
            <span>{titleName}</span>
            {detail && (
              <Badge variant="outline" className={`text-[10px] ${healthStyle(detail.health).badge}`}>
                {healthStyle(detail.health).label}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-left text-xs leading-relaxed">
            Live read from the API (SQLite + runtime checks). Refreshes every{" "}
            {HEALTH_DETAIL_POLL_MS / 1000} seconds while this dialog is open. Use this to confirm the UI matches the
            database and that the agent appears in the latest orchestration trace.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => void loadDetail()}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh now
          </Button>
          {detail?.detail_queried_at && (
            <span className="text-[11px] text-muted-foreground">
              Last query: {formatWhen(detail.detail_queried_at)}
            </span>
          )}
        </div>

        {error && (
          <p className="text-sm text-destructive flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            {error}
          </p>
        )}

        {loading && !detail && (
          <p className="text-sm text-muted-foreground">Loading detailed health…</p>
        )}

        {detail && (
          <div className="space-y-5 text-sm">
            <p className="text-muted-foreground text-xs leading-relaxed">{detail.description}</p>

            <section className="rounded-lg border border-border bg-secondary/10 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Server className="h-3.5 w-3.5" />
                Database freshness
              </p>
              <div className="grid gap-2 sm:grid-cols-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Freshness</p>
                  <Badge
                    variant="outline"
                    className={`mt-0.5 text-[10px] ${freshnessBadge(detail.decision_log.freshness_verdict).cls}`}
                  >
                    {freshnessBadge(detail.decision_log.freshness_verdict).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Age of latest row</p>
                  <p className="font-mono tabular-nums font-medium">
                    {formatDurationSeconds(detail.decision_log.staleness_seconds)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Threshold ~{formatDurationSeconds(detail.decision_log.staleness_threshold_seconds)} (autotrade-aware)
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Latest decision at</p>
                  <p className="font-mono text-[11px]">{formatWhen(detail.decision_log.last_created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Rows (24h / total)</p>
                  <p className="font-mono tabular-nums font-medium">
                    {detail.decision_log.rows_last_24h} / {detail.decision_log.total_rows}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">{detail.snapshot_alignment.note}</p>
              <div className="text-[11px] space-y-1 font-mono text-muted-foreground border-t border-border/60 pt-2">
                <p>
                  <span className="text-foreground/80">detail_queried_at:</span>{" "}
                  {formatWhen(detail.snapshot_alignment.detail_queried_at)}
                </p>
                <p>
                  <span className="text-foreground/80">fleet_snapshot_generated_at:</span>{" "}
                  {formatWhen(detail.snapshot_alignment.fleet_snapshot_generated_at)}
                </p>
                <p>
                  <span className="text-foreground/80">db_last_decision_at:</span>{" "}
                  {formatWhen(detail.snapshot_alignment.db_last_decision_at)}
                </p>
              </div>
            </section>

            <section className="rounded-lg border border-border bg-secondary/10 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" />
                Dashboard card vs database
              </p>
              <p className="text-xs leading-relaxed">{detail.client_card_alignment.message}</p>
              {detail.client_card_alignment.matches_db_last_run === false && (
                <p className="text-xs text-warning">
                  The fleet card&apos;s &quot;Last run&quot; does not match SQLite within the tolerance window. Trigger a
                  dashboard refresh or wait for the automatic poll.
                </p>
              )}
            </section>

            <section className="rounded-lg border border-border bg-secondary/10 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                Orchestration pipeline
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{detail.orchestration_feed.explain}</p>
              <div className="grid gap-2 sm:grid-cols-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Latest cycle logged</p>
                  <p className="font-mono text-[11px]">{formatWhen(detail.orchestration_feed.latest_cycle_logged_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Cycle symbol</p>
                  <p className="font-mono">{detail.orchestration_feed.latest_cycle_symbol || "—"}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant="outline"
                  className={
                    detail.orchestration_feed.this_agent_in_latest_trace
                      ? "text-[10px] text-positive border-positive/40 bg-positive/10"
                      : "text-[10px] text-warning border-warning/40 bg-warning/10"
                  }
                >
                  {detail.orchestration_feed.this_agent_in_latest_trace
                    ? "Seen in latest trace"
                    : "Not in latest trace"}
                </Badge>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Agents in trace: {detail.orchestration_feed.agents_in_latest_trace.join(", ") || "—"}
              </p>
              {detail.orchestration_feed.trace_error_steps.length > 0 && (
                <div className="rounded border border-warning/40 bg-warning/5 p-2 text-[11px]">
                  <p className="font-medium text-warning mb-1">Non-ok trace steps</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {detail.orchestration_feed.trace_error_steps.map((s, i) => (
                      <li key={`${s.timestamp}-${i}`}>
                        <span className="font-mono">{s.agent}</span> · {s.stage} ·{" "}
                        <span className="text-warning">{s.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section className="rounded-lg border border-border bg-secondary/10 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                LLM / OpenAI
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge
                  variant="outline"
                  className={
                    detail.llm.openai_configured
                      ? "text-[10px] text-positive border-positive/40 bg-positive/10"
                      : "text-[10px] text-warning border-warning/40 bg-warning/10"
                  }
                >
                  OpenAI {detail.llm.openai_configured ? "configured" : "not configured"}
                </Badge>
                {detail.llm.model ? (
                  <span className="text-muted-foreground font-mono text-[11px]">model: {detail.llm.model}</span>
                ) : null}
              </div>
              {detail.llm.hints.length > 0 ? (
                <ul className="text-[11px] text-warning list-disc pl-4 space-y-0.5">
                  {detail.llm.hints.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-muted-foreground">No recent LLM-error hints in sampled payloads.</p>
              )}
            </section>

            <section className="rounded-lg border border-border bg-secondary/10 p-3 space-y-2 text-xs">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Autotrade scheduler</p>
              <p>
                Enabled:{" "}
                <span className="font-medium">{detail.autotrade.enabled ? "yes" : "no"}</span> · Interval:{" "}
                <span className="font-mono">{detail.autotrade.interval_minutes}m</span>
              </p>
              <p className="text-muted-foreground">
                Last run: {formatWhen(detail.autotrade.last_run_at ?? undefined)}
              </p>
              {detail.autotrade.last_error ? (
                <p className="text-destructive text-[11px]">Last error: {String(detail.autotrade.last_error)}</p>
              ) : null}
            </section>

            {detail.health_notes && detail.health_notes.length > 0 && (
              <section>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Runtime health notes
                </p>
                <ul className="text-xs text-warning list-disc pl-4 space-y-0.5">
                  {detail.health_notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Recent decision rows (SQLite)
              </p>
              <ScrollArea className="h-[min(220px,32vh)] rounded border border-border">
                <div className="p-2 space-y-2">
                  {detail.recent_decisions.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-1">No rows for this agent.</p>
                  ) : (
                    detail.recent_decisions.map((r) => (
                      <div key={r.id} className="rounded-md bg-card/80 border border-border/60 p-2 text-[11px]">
                        <div className="flex flex-wrap justify-between gap-1 font-mono text-muted-foreground">
                          <span>#{r.id}</span>
                          <span>{formatWhen(r.created_at)}</span>
                        </div>
                        <p className="font-medium text-foreground mt-0.5">{r.symbol}</p>
                        <p className="text-muted-foreground mt-1">Keys: {r.summary.top_level_keys.join(", ")}</p>
                        {r.summary.notes.length > 0 && (
                          <ul className="mt-1 list-disc pl-3 text-warning/90">
                            {r.summary.notes.map((n, i) => (
                              <li key={i}>{n}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function AgentStatus({
  fleet,
  loading,
}: {
  fleet?: AgentFleetResponse | null
  loading?: boolean
}) {
  const agents = fleet?.agents ?? []
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailAgentKey, setDetailAgentKey] = useState<string | null>(null)

  const openDetail = (key: string) => {
    setDetailAgentKey(key)
    setDetailOpen(true)
  }

  return (
    <>
      <Card className="min-w-0 w-full bg-card border-border lg:col-span-1">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Agent Fleet Status
          </CardTitle>
          {fleet?.generated_at && (
            <p className="text-xs text-muted-foreground">
              Snapshot {formatWhen(fleet.generated_at)} · Scheduled trades in DB: {fleet.scheduled_trades_total}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground leading-snug">
            Click an agent for a live health modal (DB alignment, orchestration trace, LLM, autotrade).
          </p>
        </CardHeader>
        <CardContent>
          {loading && agents.length === 0 && (
            <p className="text-sm text-muted-foreground">Loading agent fleet…</p>
          )}
          {!loading && agents.length === 0 && (
            <p className="text-sm text-muted-foreground">No fleet data returned from the API.</p>
          )}
          <ScrollArea className="h-[min(420px,50vh)] pr-3">
            <div className="grid gap-3 sm:grid-cols-2">
              {agents.map((agent) => {
                const fb = freshnessBadge(agent.freshness_verdict ?? "never_run")
                const freshnessVerdict = agent.freshness_verdict ?? "never_run"
                const FreshnessIcon =
                  freshnessVerdict === "fresh"
                    ? Activity
                    : freshnessVerdict === "stale"
                      ? AlertCircle
                      : Clock
                return (
                  <div
                    key={agent.agent_key}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetail(agent.agent_key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openDetail(agent.agent_key)
                      }
                    }}
                    className="rounded-lg border border-border/80 bg-secondary/20 p-3 space-y-2 cursor-pointer text-left outline-none transition-colors hover:border-primary/35 hover:bg-secondary/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm leading-tight truncate">{agent.display_name}</h3>
                        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{agent.description}</p>
                      </div>
                      <Badge variant="outline" className={`shrink-0 text-[10px] ${fb.cls}`}>
                        <FreshnessIcon className="h-3 w-3 mr-1" />
                        {fb.label}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-muted-foreground">Runs (logged)</p>
                        <p className="font-mono font-semibold tabular-nums">{agent.execution_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last run</p>
                        <p className="font-mono text-[11px] leading-tight flex items-center gap-1">
                          <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                          <span className="truncate">{formatWhen(agent.last_executed_at)}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">
                        {agent.has_memory ? (
                          <>
                            {agent.memory_kind === "files" ? (
                              <HardDrive className="h-3 w-3 mr-1" />
                            ) : (
                              <Database className="h-3 w-3 mr-1" />
                            )}
                            Memory: {agent.memory_kind}
                          </>
                        ) : (
                          <>No memory yet</>
                        )}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-snug">{agent.memory_hint}</p>
                    {agent.health_notes && agent.health_notes.length > 0 && (
                      <ul className="text-[10px] text-warning space-y-0.5 list-disc pl-3">
                        {agent.health_notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          </ScrollArea>

          <CycleProgressPanel />

          {(fleet?.narratives?.orchestration_agent?.text || fleet?.narratives?.execution_agent?.text) && (
            <div className="mt-4 min-w-0 space-y-3 border-t border-border pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Orchestrator and execution — latest thinking
              </p>
              {fleet?.narratives?.orchestration_agent?.text && (
                <FleetNarrativePanel
                  label="Orchestration"
                  updatedAt={fleet.narratives.orchestration_agent.updated_at}
                  body={fleet.narratives.orchestration_agent.text}
                />
              )}
              {fleet?.narratives?.execution_agent?.text && (
                <FleetNarrativePanel
                  label="Execution"
                  updatedAt={fleet.narratives.execution_agent.updated_at}
                  body={fleet.narratives.execution_agent.text}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AgentHealthModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        agentKey={detailAgentKey}
        fleet={fleet}
      />
    </>
  )
}
